import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { 
  Plus, Edit, Trash2, Check, DollarSign, Users, Phone, 
  MessageSquare, Clock, Star, Zap, Headphones, Package,
  Crown, Link2, Copy, ExternalLink, Search, Loader2,
  Building2, Tag
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// ─── Standard Plan Form Defaults ───
const defaultPlanForm = {
  name: "", price: 0, billing_cycle: "monthly", features: [],
  max_users: 2, unlimited_technicians: true, sms_limit: 1000,
  minutes_limit: 750, dedicated_lines: 2, call_masking: false,
  joby_automation: false, quickbooks_integration: false,
  customer_portal: false, inventory_management: false,
  chat_support_24_7: true, is_active: true, is_popular: false,
  plan_tier: "single", max_locations: 1,
  is_contact_us: false, contact_us_text: "",
  apply_to_existing: false,
};

const defaultMultiLocationForm = {
  ...defaultPlanForm,
  plan_tier: "multi_location",
  max_locations: 2,
  max_users: 6,
  sms_limit: 4000,
  minutes_limit: 3000,
  dedicated_lines: 4,
  call_masking: true,
  joby_automation: true,
  quickbooks_integration: true,
  customer_portal: true,
  inventory_management: true,
  is_contact_us: true,  // Multi-location plans default to "Contact us" until price is set
  contact_us_text: "Get a custom quote tailored to your business",
};

// ─── Shared Plan Feature Form ───
const PlanFeatureForm = ({ formData, setFormData }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-muted-foreground">Plan Name</Label>
        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="bg-theme-surface border-theme text-foreground mt-1" placeholder="e.g., Enterprise VIP" />
      </div>
      <div>
        <Label className="text-muted-foreground">Price ($/month)</Label>
        <div className="relative mt-1">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="number" value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
            className="bg-theme-surface border-theme text-foreground pl-9" />
        </div>
      </div>
    </div>

    <div>
      <Label className="text-muted-foreground">Billing Cycle</Label>
      <Select value={formData.billing_cycle} onValueChange={(v) => setFormData({ ...formData, billing_cycle: v })}>
        <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-popover border-theme">
          <SelectItem value="monthly">Monthly</SelectItem>
          <SelectItem value="yearly">Yearly</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Limits */}
    <div className="bg-muted/50 rounded-lg p-4 space-y-4">
      <h3 className="text-foreground font-semibold flex items-center gap-2">
        <Users className="w-4 h-4 text-blue-400" /> Limits
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-muted-foreground">Max Users</Label>
          <Input type="number" value={formData.max_users} min={1}
            onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 1 })}
            className="bg-theme-surface border-theme text-foreground mt-1" />
        </div>
        <div>
          <Label className="text-muted-foreground flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-emerald-500" /> Max Locations
          </Label>
          <Input type="number" value={formData.max_locations} min={1}
            onChange={(e) => setFormData({ ...formData, max_locations: parseInt(e.target.value) || 1 })}
            className="bg-theme-surface border-theme text-foreground mt-1"
            data-testid="plan-max-locations-input" />
          <p className="text-[11px] text-muted-foreground mt-1">
            {formData.plan_tier === "multi_location"
              ? "How many locations this multi-location plan allows"
              : "Single-location plans should keep this at 1"}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between bg-theme-surface rounded-lg p-3">
        <Label className="text-foreground">Unlimited Technicians</Label>
        <Switch checked={formData.unlimited_technicians}
          onCheckedChange={(c) => setFormData({ ...formData, unlimited_technicians: c })} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-muted-foreground">SMS Limit</Label>
          <Input type="number" value={formData.sms_limit} min={0}
            onChange={(e) => setFormData({ ...formData, sms_limit: parseInt(e.target.value) || 0 })}
            className="bg-theme-surface border-theme text-foreground mt-1" />
        </div>
        <div>
          <Label className="text-muted-foreground">Minutes Limit</Label>
          <Input type="number" value={formData.minutes_limit} min={0}
            onChange={(e) => setFormData({ ...formData, minutes_limit: parseInt(e.target.value) || 0 })}
            className="bg-theme-surface border-theme text-foreground mt-1" />
        </div>
        <div>
          <Label className="text-muted-foreground">Dedicated Lines</Label>
          <Input type="number" value={formData.dedicated_lines} min={0}
            onChange={(e) => setFormData({ ...formData, dedicated_lines: parseInt(e.target.value) || 0 })}
            className="bg-theme-surface border-theme text-foreground mt-1" />
        </div>
      </div>
    </div>

    {/* Feature Toggles */}
    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
      <h3 className="text-foreground font-semibold flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#FFCC00]" /> Features
      </h3>
      {[
        { key: "call_masking", label: "Call Masking", icon: <Phone className="w-4 h-4 text-purple-400" /> },
        { key: "joby_automation", label: "FIELDZ Automation", icon: <Zap className="w-4 h-4 text-[#FFCC00]" /> },
        { key: "quickbooks_integration", label: "QuickBooks Integration", icon: <Building2 className="w-4 h-4 text-green-500" /> },
        { key: "customer_portal", label: "Customer Portal", icon: <Users className="w-4 h-4 text-blue-400" /> },
        { key: "inventory_management", label: "Inventory Management", icon: <Package className="w-4 h-4 text-orange-400" /> },
        { key: "chat_support_24_7", label: "24/7 Chat Support", icon: <Headphones className="w-4 h-4 text-green-400" /> },
      ].map(({ key, label, icon }) => (
        <div key={key} className="flex items-center justify-between">
          <div className="flex items-center gap-2">{icon}<Label className="text-foreground">{label}</Label></div>
          <Switch checked={formData[key]} onCheckedChange={(c) => setFormData({ ...formData, [key]: c })} />
        </div>
      ))}
    </div>

    {/* Stripe Sync Options */}
    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
      <h3 className="text-foreground font-semibold flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-emerald-500" /> Stripe & Billing
      </h3>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-foreground">Apply price change to existing subscribers</Label>
          <p className="text-xs text-muted-foreground mt-0.5">If enabled, active Stripe subscriptions will be updated to the new price</p>
        </div>
        <Switch checked={formData.apply_to_existing} onCheckedChange={(c) => setFormData({ ...formData, apply_to_existing: c })} data-testid="plan-apply-existing-switch" />
      </div>
    </div>

    {/* Landing Page Display */}
    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
      <h3 className="text-foreground font-semibold flex items-center gap-2">
        <Headphones className="w-4 h-4 text-blue-400" /> Landing Page Display
      </h3>
      <div className="flex items-center justify-between">
        <div className="flex-1 pr-3">
          <Label className="text-foreground">"Contact us" mode</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Hide the price on the landing page and show a "Book a Demo" button instead of direct signup.
            When off, the plan card shows the price and a "Start Free Trial" button that pre-selects this plan.
          </p>
        </div>
        <Switch
          checked={!!formData.is_contact_us}
          onCheckedChange={(c) => setFormData({ ...formData, is_contact_us: c })}
          data-testid="plan-is-contact-us-switch"
        />
      </div>
      {formData.is_contact_us && (
        <div>
          <Label className="text-muted-foreground text-xs">Text shown instead of price (optional)</Label>
          <Input
            value={formData.contact_us_text || ""}
            onChange={(e) => setFormData({ ...formData, contact_us_text: e.target.value })}
            className="bg-theme-surface border-theme text-foreground mt-1"
            placeholder="e.g. Custom quote · Let's talk"
            maxLength={80}
            data-testid="plan-contact-us-text-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Leave blank to show the default "Contact us" label.
          </p>
        </div>
      )}
    </div>
  </div>
);


// ─── Plan Card (shared) ───
const PlanCard = ({ plan, openEditDialog, deletePlan, accent = "#FFCC00" }) => (
  <Card className={`glass-card relative overflow-hidden ${plan.is_popular ? `border-[${accent}]/50 ring-2 ring-[${accent}]/20` : 'border-theme'} ${!plan.is_active ? 'opacity-60' : ''}`}
    style={plan.is_popular ? { borderColor: `${accent}80`, boxShadow: `0 0 0 2px ${accent}20` } : {}}>
    {plan.is_popular && <div className="absolute top-0 left-0 right-0 text-xs font-bold text-center py-1" style={{ background: accent, color: "#000" }}>MOST POPULAR</div>}
    {!plan.is_active && <Badge className="absolute top-4 right-4 bg-gray-500/20 text-gray-400">Inactive</Badge>}
    <CardHeader className={`pb-2 ${plan.is_popular ? 'pt-8' : ''}`}>
      <div className="flex items-center justify-between">
        <CardTitle className="text-foreground text-xl font-['Manrope']">{plan.name}</CardTitle>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEditDialog(plan)} className="h-8 w-8 text-blue-400 hover:text-blue-300" data-testid={`edit-plan-${plan.plan_id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => deletePlan(plan.plan_id)} className="h-8 w-8 text-red-400 hover:text-red-300" data-testid={`delete-plan-${plan.plan_id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold" style={{ color: accent }}>${plan.price}</span>
          {Number(plan.price) === 0 && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] ml-2">TODO: set price</Badge>}
          {plan.is_contact_us && <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px] ml-2">Contact Us mode</Badge>}
        </div>
        <p className="text-muted-foreground text-sm">Billed {plan.billing_cycle}</p>
        {plan.stripe_product_id ? (
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 text-xs mt-1">Stripe Synced</Badge>
        ) : (
          <Badge variant="outline" className="border-amber-500/30 text-amber-500 text-xs mt-1">Not synced</Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 py-3 border-y border-theme">
        <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-500" /><span className="text-foreground text-sm font-semibold">{plan.max_locations === -1 ? "\u221E" : `Up to ${plan.max_locations || 1}`} location{plan.max_locations === 1 ? "" : "s"}</span></div>
        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" /><span className="text-foreground text-sm">{plan.max_users || 2} users</span></div>
        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-green-400" /><span className="text-foreground text-sm">{plan.unlimited_technicians ? "\u221E" : "Limited"} techs</span></div>
        <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-purple-400" /><span className="text-foreground text-sm">{(plan.sms_limit || 0).toLocaleString()} SMS</span></div>
        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /><span className="text-foreground text-sm">{(plan.minutes_limit || 0).toLocaleString()} min</span></div>
        <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-cyan-400" /><span className="text-foreground text-sm">{plan.dedicated_lines || 0} lines</span></div>
      </div>
      <ul className="space-y-2">
        {plan.call_masking && <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-400" /><span className="text-foreground">Call masking</span></li>}
        {plan.joby_automation && <li className="flex items-center gap-2 text-sm"><Zap className="w-4 h-4 text-[#FFCC00]" /><span className="text-foreground">FIELDZ Automation</span></li>}
        {plan.inventory_management && <li className="flex items-center gap-2 text-sm"><Package className="w-4 h-4 text-orange-400" /><span className="text-foreground">Inventory Management</span></li>}
        {plan.chat_support_24_7 && <li className="flex items-center gap-2 text-sm"><Headphones className="w-4 h-4 text-green-400" /><span className="text-foreground">24/7 Chat support</span></li>}
      </ul>
    </CardContent>
  </Card>
);


// ─── Multi-Location Plans Section ───
const MultiLocationPlansSection = ({ plans, loading, openCreateDialog, openEditDialog, deletePlan }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-xl font-bold text-foreground font-['Manrope'] flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-500" /> Multi-Location Plans
        </h2>
        <p className="text-muted-foreground text-sm">Plans for businesses operating multiple locations under one account</p>
      </div>
      <Button onClick={openCreateDialog} className="bg-emerald-600 text-white hover:bg-emerald-600/90" data-testid="create-multi-location-plan-btn">
        <Plus className="w-4 h-4 mr-2" /> Create Multi-Location Plan
      </Button>
    </div>

    {loading ? (
      <div className="text-muted-foreground text-center py-12">Loading plans...</div>
    ) : plans.length === 0 ? (
      <Card className="glass-card border-theme border-dashed">
        <CardContent className="p-10 text-center">
          <Building2 className="w-10 h-10 mx-auto text-emerald-500/40 mb-3" />
          <p className="text-muted-foreground mb-1">No multi-location plans yet</p>
          <p className="text-muted-foreground text-sm mb-4">Create tiered plans for customers with multiple branches (locksmith chains, multi-office HVAC, etc.)</p>
          <Button onClick={openCreateDialog} className="bg-emerald-600 text-white"><Plus className="w-4 h-4 mr-2" /> Create Multi-Location Plan</Button>
        </CardContent>
      </Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard key={plan.plan_id} plan={plan} openEditDialog={openEditDialog} deletePlan={deletePlan} accent="#10b981" />
        ))}
      </div>
    )}
  </div>
);


// ─── Standard Plans Section ───
const StandardPlansSection = ({ plans, loading, openCreateDialog, openEditDialog, deletePlan }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-xl font-bold text-foreground font-['Manrope']">Single-Location Plans</h2>
        <p className="text-muted-foreground text-sm">Public plans for businesses operating from one location</p>
      </div>
      <Button onClick={openCreateDialog} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80" data-testid="create-standard-plan-btn">
        <Plus className="w-4 h-4 mr-2" /> Create Plan
      </Button>
    </div>

    {loading ? (
      <div className="text-muted-foreground text-center py-12">Loading plans...</div>
    ) : plans.length === 0 ? (
      <Card className="glass-card border-theme">
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground mb-4">No single-location plans yet</p>
          <Button onClick={openCreateDialog} className="bg-[#0046BE]"><Plus className="w-4 h-4 mr-2" /> Create Plan</Button>
        </CardContent>
      </Card>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard key={plan.plan_id} plan={plan} openEditDialog={openEditDialog} deletePlan={deletePlan} accent="#FFCC00" />
        ))}
      </div>
    )}
  </div>
);


// ─── Custom Plans Section ───
const CustomPlansSection = ({ customPlans, loading, openCustomDialog, deleteCustomPlan, generateCheckoutLink, openEditLimits }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-xl font-bold text-foreground font-['Manrope'] flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#FFCC00]" /> Custom Plans
        </h2>
        <p className="text-muted-foreground text-sm">Private plans with custom pricing for specific users &mdash; auto-synced to Stripe</p>
      </div>
      <Button onClick={openCustomDialog} className="bg-gradient-to-r from-[#FFCC00] to-amber-500 text-black hover:opacity-90 font-semibold" data-testid="create-custom-plan-btn">
        <Plus className="w-4 h-4 mr-2" /> Create Custom Plan
      </Button>
    </div>

    {loading ? (
      <div className="text-muted-foreground text-center py-8">Loading custom plans...</div>
    ) : customPlans.length === 0 ? (
      <Card className="glass-card border-theme border-dashed">
        <CardContent className="p-10 text-center">
          <Crown className="w-10 h-10 mx-auto text-[#FFCC00]/40 mb-3" />
          <p className="text-muted-foreground mb-1">No custom plans yet</p>
          <p className="text-muted-foreground text-sm mb-4">Create a custom-priced plan for a specific user. A Stripe product & subscription price are created automatically.</p>
          <Button onClick={openCustomDialog} className="bg-gradient-to-r from-[#FFCC00] to-amber-500 text-black font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Create Custom Plan
          </Button>
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-3">
        {customPlans.map((plan) => (
          <Card key={plan.plan_id} className="glass-card border-theme hover:border-[#FFCC00]/30 transition-colors" data-testid={`custom-plan-${plan.plan_id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Left: Plan info */}
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#FFCC00]/20 to-amber-500/10 border border-[#FFCC00]/20 flex items-center justify-center shrink-0">
                    <Crown className="w-6 h-6 text-[#FFCC00]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-semibold">{plan.name}</span>
                      <Badge className="bg-[#FFCC00]/10 text-[#FFCC00] border-[#FFCC00]/20 text-xs">
                        ${plan.price}/{plan.billing_cycle === "yearly" ? "yr" : "mo"}
                      </Badge>
                      {plan.user_on_this_plan && (
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-xs">Active</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                      <span className="truncate">{plan.assigned_user_name}</span>
                      <span className="text-muted-foreground/40">&middot;</span>
                      <span className="truncate">{plan.assigned_user_email}</span>
                      {plan.assigned_user_company && (
                        <>
                          <span className="text-muted-foreground/40">&middot;</span>
                          <span className="truncate">{plan.assigned_user_company}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Limits summary + actions */}
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{plan.max_users}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{(plan.sms_limit || 0).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{(plan.minutes_limit || 0).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{plan.dedicated_lines}</span>
                    <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{plan.max_locations ?? 1}</span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={() => openEditLimits(plan)}
                      data-testid={`edit-limits-${plan.plan_id}`}
                    >
                      <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit Limits
                    </Button>
                    <Button variant="outline" size="sm"
                      className="h-8 px-3 text-xs border-[#FFCC00]/30 text-[#FFCC00] hover:bg-[#FFCC00]/10"
                      onClick={() => generateCheckoutLink(plan.plan_id)}
                      data-testid={`checkout-link-${plan.plan_id}`}
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1.5" /> Payment Link
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                      onClick={() => deleteCustomPlan(plan.plan_id)}
                      data-testid={`delete-custom-${plan.plan_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);


// ─── Location-Limit Grace Period Settings ───
const LocationLimitSettingsPanel = () => {
  const [graceDays, setGraceDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/system-settings/location-limits`, getAuthConfig())
      .then(res => setGraceDays(res.data.grace_days ?? 30))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/system-settings/location-limits`,
        { grace_days: parseInt(graceDays) || 0 }, getAuthConfig());
      toast.success("Grace period updated");
    } catch {
      toast.error("Failed to save grace period");
    } finally { setSaving(false); }
  };

  return (
    <Card className="glass-card border-theme">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-5 h-5 text-emerald-500" />
          <h3 className="text-lg font-semibold text-foreground font-['Manrope']">Location-Limit Enforcement</h3>
        </div>
        <p className="text-muted-foreground text-sm mb-4">
          When a user downgrades to a plan that allows fewer locations than they currently have,
          they enter a grace period. During this window, existing locations remain readable and
          adding new ones is blocked. Once the grace period ends, feature restrictions kick in.
          Locations are never auto-deleted.
        </p>
        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-xs">
            <Label className="text-muted-foreground text-xs">Grace Period (days)</Label>
            <Input type="number" value={graceDays} min={0} disabled={loading}
              onChange={(e) => setGraceDays(parseInt(e.target.value) || 0)}
              className="bg-theme-surface border-theme text-foreground mt-1"
              data-testid="grace-days-input" />
          </div>
          <Button onClick={save} disabled={saving || loading}
            className="bg-emerald-600 text-white hover:bg-emerald-600/90"
            data-testid="save-grace-days-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ─── Main Component ───
const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [customPlans, setCustomPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customLoading, setCustomLoading] = useState(true);

  // Standard plan dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ ...defaultPlanForm });

  // Custom plan dialog
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customFormData, setCustomFormData] = useState({ ...defaultPlanForm, notes: "" });
  const [customSaving, setCustomSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Custom-plan limits dialog (edit limits of an active custom plan in-place)
  const [limitsDialogOpen, setLimitsDialogOpen] = useState(false);
  const [limitsEditingPlan, setLimitsEditingPlan] = useState(null);
  const [limitsForm, setLimitsForm] = useState({});
  const [limitsSaving, setLimitsSaving] = useState(false);

  // Checkout link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkPlanId, setLinkPlanId] = useState("");
  const [linkPromoCode, setLinkPromoCode] = useState("");
  const [linkPromoApplied, setLinkPromoApplied] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/plans`, getAuthConfig());
      setPlans(res.data.plans);
    } catch (error) {
      if (error.response?.status !== 401) toast.error("Failed to load plans");
    } finally { setLoading(false); }
  }, []);

  const fetchCustomPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/custom-plans`, getAuthConfig());
      setCustomPlans(res.data.plans);
    } catch (error) {
      if (error.response?.status !== 401) toast.error("Failed to load custom plans");
    } finally { setCustomLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    if (usersLoaded) return;
    try {
      const res = await axios.get(`${API}/admin/users-list`, getAuthConfig());
      const users = res.data.users || res.data || [];
      setAllUsers(users);
      setUsersLoaded(true);
    } catch { /* silent */ }
  }, [usersLoaded]);

  useEffect(() => { fetchPlans(); fetchCustomPlans(); }, [fetchPlans, fetchCustomPlans]);

  // Filter users based on search
  useEffect(() => {
    if (!userSearch.trim()) { setUserResults([]); return; }
    const q = userSearch.toLowerCase();
    setUserResults(allUsers.filter(u =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.company_name || "").toLowerCase().includes(q)
    ).slice(0, 8));
  }, [userSearch, allUsers]);

  // ─── Standard Plan CRUD ───
  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormData({ ...defaultPlanForm });
    setDialogOpen(true);
  };

  const openCreateMultiLocationDialog = () => {
    setEditingPlan(null);
    setFormData({ ...defaultMultiLocationForm });
    setDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name, price: plan.price, billing_cycle: plan.billing_cycle,
      features: plan.features || [], max_users: plan.max_users || 2,
      unlimited_technicians: plan.unlimited_technicians !== false,
      sms_limit: plan.sms_limit || 1000, minutes_limit: plan.minutes_limit || 750,
      dedicated_lines: plan.dedicated_lines || 2, call_masking: plan.call_masking || false,
      joby_automation: plan.joby_automation || false, quickbooks_integration: plan.quickbooks_integration || false,
      customer_portal: plan.customer_portal || false, inventory_management: plan.inventory_management || false,
      chat_support_24_7: plan.chat_support_24_7 !== false, is_active: plan.is_active !== false,
      is_popular: plan.is_popular || false,
      plan_tier: plan.plan_tier || "single",
      max_locations: plan.max_locations || 1,
      is_contact_us: !!plan.is_contact_us,
      contact_us_text: plan.contact_us_text || "",
      apply_to_existing: false,
    });
    setDialogOpen(true);
  };

  const savePlan = async () => {
    if (!formData.name) { toast.error("Plan name is required"); return; }
    const autoFeatures = [];
    if (formData.plan_tier === "multi_location") {
      autoFeatures.push(`Up to ${formData.max_locations} locations`);
    }
    autoFeatures.push(
      `${formData.max_users} users`,
      formData.unlimited_technicians ? "Unlimited technicians" : "Limited technicians",
      `${formData.sms_limit.toLocaleString()} SMS`,
      `${formData.minutes_limit.toLocaleString()} minutes`,
      `${formData.dedicated_lines} dedicated lines`,
    );
    if (formData.plan_tier === "multi_location") {
      autoFeatures.push(
        "Centralized multi-location dispatch",
        "Unified cross-location reporting",
        "Single billing for all locations",
        "Location-based permissions",
      );
    }
    if (formData.call_masking) autoFeatures.push("Call masking");
    if (formData.joby_automation) autoFeatures.push("FIELDZ Automation");
    if (formData.quickbooks_integration) autoFeatures.push("QuickBooks Integration");
    if (formData.customer_portal) autoFeatures.push("Customer Portal");
    if (formData.inventory_management) autoFeatures.push("Inventory Management");
    if (formData.chat_support_24_7) autoFeatures.push("24/7 Chat support");
    const dataToSend = { ...formData, features: autoFeatures, max_team_members: formData.max_users };
    try {
      let res;
      if (editingPlan) {
        res = await axios.put(`${API}/admin/plans/${editingPlan.plan_id}`, dataToSend, getAuthConfig());
        toast.success("Plan updated & synced to Stripe");
      } else {
        res = await axios.post(`${API}/admin/plans`, dataToSend, getAuthConfig());
        toast.success("Plan created & synced to Stripe");
      }
      if (res.data.stripe_error) {
        toast.warning(`Stripe sync issue: ${res.data.stripe_error}`, { duration: 6000 });
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save plan"); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      await axios.delete(`${API}/admin/plans/${planId}`, getAuthConfig());
      toast.success("Plan deleted");
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete plan"); }
  };

  // ─── Custom Plan CRUD ───
  const openCustomDialog = () => {
    setCustomFormData({ ...defaultPlanForm, notes: "" });
    setSelectedUser(null);
    setUserSearch("");
    setUserResults([]);
    setCustomDialogOpen(true);
    fetchUsers();
  };

  // ── Edit Limits (active custom plans) ──
  const openEditLimits = (plan) => {
    setLimitsEditingPlan(plan);
    setLimitsForm({
      max_users: plan.max_users ?? 2,
      sms_limit: plan.sms_limit ?? 1000,
      minutes_limit: plan.minutes_limit ?? 750,
      dedicated_lines: plan.dedicated_lines ?? 2,
      max_locations: plan.max_locations ?? 1,
      notes: plan.notes ?? "",
    });
    setLimitsDialogOpen(true);
  };

  const saveLimits = async () => {
    if (!limitsEditingPlan) return;
    setLimitsSaving(true);
    try {
      await axios.put(
        `${API}/admin/custom-plans/${limitsEditingPlan.plan_id}/limits`,
        {
          max_users: parseInt(limitsForm.max_users) || 0,
          sms_limit: parseInt(limitsForm.sms_limit) || 0,
          minutes_limit: parseInt(limitsForm.minutes_limit) || 0,
          dedicated_lines: parseInt(limitsForm.dedicated_lines) || 0,
          max_locations: parseInt(limitsForm.max_locations) || 1,
          notes: limitsForm.notes || "",
        },
        getAuthConfig(),
      );
      toast.success("Limits updated");
      setLimitsDialogOpen(false);
      fetchCustomPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update limits");
    } finally {
      setLimitsSaving(false);
    }
  };

  const saveCustomPlan = async () => {
    if (!customFormData.name) { toast.error("Plan name is required"); return; }
    if (!selectedUser) { toast.error("Please select a user"); return; }
    if (customFormData.price <= 0) { toast.error("Price must be greater than 0"); return; }

    setCustomSaving(true);
    try {
      await axios.post(`${API}/admin/custom-plans`, {
        ...customFormData,
        assigned_user_id: selectedUser.user_id,
        max_team_members: customFormData.max_users,
      }, getAuthConfig());
      toast.success("Custom plan created & Stripe product synced!");
      setCustomDialogOpen(false);
      fetchCustomPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create custom plan");
    } finally { setCustomSaving(false); }
  };

  const deleteCustomPlan = async (planId) => {
    if (!window.confirm("Delete this custom plan? The Stripe product will be archived.")) return;
    try {
      await axios.delete(`${API}/admin/custom-plans/${planId}`, getAuthConfig());
      toast.success("Custom plan deleted");
      fetchCustomPlans();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
  };

  const generateCheckoutLink = async (planId) => {
    setLinkPlanId(planId);
    setCheckoutUrl("");
    setLinkPromoCode("");
    setLinkPromoApplied(null);
    setLinkDialogOpen(true);
  };

  const confirmGenerateLink = async () => {
    setLinkLoading(true);
    setCheckoutUrl("");
    setLinkPromoApplied(null);
    try {
      const body = {};
      if (linkPromoCode.trim()) body.promo_code = linkPromoCode.trim();
      const res = await axios.post(`${API}/admin/custom-plans/${linkPlanId}/checkout-link`, body, getAuthConfig());
      setCheckoutUrl(res.data.checkout_url);
      setLinkPromoApplied(res.data.promo_applied || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate link");
      if (!error.response?.data?.detail?.includes("romo")) {
        setLinkDialogOpen(false);
      }
    } finally { setLinkLoading(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(checkoutUrl);
    toast.success("Payment link copied!");
  };

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Subscription Plans</h1>
        <p className="text-muted-foreground">Manage single-location, multi-location, and custom plans</p>
      </div>

      {/* Location-Limit System Settings */}
      <LocationLimitSettingsPanel />

      {/* Custom Plans First (priority) */}
      <CustomPlansSection
        customPlans={customPlans} loading={customLoading}
        openCustomDialog={openCustomDialog}
        deleteCustomPlan={deleteCustomPlan}
        generateCheckoutLink={generateCheckoutLink}
        openEditLimits={openEditLimits}
      />

      {/* Separator */}
      <div className="border-t border-theme" />

      {/* Multi-Location Plans */}
      <MultiLocationPlansSection
        plans={plans.filter(p => p.plan_tier === "multi_location")}
        loading={loading}
        openCreateDialog={openCreateMultiLocationDialog}
        openEditDialog={openEditDialog}
        deletePlan={deletePlan}
      />

      {/* Separator */}
      <div className="border-t border-theme" />

      {/* Single-Location Plans */}
      <StandardPlansSection
        plans={plans.filter(p => (p.plan_tier || "single") === "single")}
        loading={loading}
        openCreateDialog={openCreateDialog}
        openEditDialog={openEditDialog}
        deletePlan={deletePlan}
      />

      {/* ─── Standard Plan Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              {formData.plan_tier === "multi_location" && <Building2 className="w-5 h-5 text-emerald-500" />}
              {editingPlan
                ? `Edit ${formData.plan_tier === "multi_location" ? "Multi-Location" : "Single-Location"} Plan`
                : `Create New ${formData.plan_tier === "multi_location" ? "Multi-Location" : "Single-Location"} Plan`}
            </DialogTitle>
          </DialogHeader>
          <PlanFeatureForm formData={formData} setFormData={setFormData} />
          {/* Plan Settings */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h3 className="text-foreground font-semibold">Plan Settings</h3>
            <div className="flex items-center justify-between">
              <Label className="text-foreground">Plan Active</Label>
              <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#FFCC00]" />
                <Label className="text-foreground">Mark as "Most Popular"</Label>
              </div>
              <Switch checked={formData.is_popular} onCheckedChange={(c) => setFormData({ ...formData, is_popular: c })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-theme text-foreground">Cancel</Button>
            <Button onClick={savePlan} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80">
              {editingPlan ? "Save Changes" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Custom Plan Dialog ─── */}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#FFCC00]" /> Create Custom Plan
            </DialogTitle>
          </DialogHeader>

          {/* User Search */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h3 className="text-foreground font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" /> Assign to User
            </h3>
            {selectedUser ? (
              <div className="flex items-center justify-between bg-theme-surface rounded-lg p-3 border border-theme">
                <div>
                  <span className="text-foreground font-medium">{selectedUser.name}</span>
                  <span className="text-muted-foreground text-sm ml-2">{selectedUser.email}</span>
                  {selectedUser.company_name && <span className="text-muted-foreground text-sm ml-2">({selectedUser.company_name})</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setUserSearch(""); }}
                  className="text-red-400 hover:text-red-300 h-7 px-2">Change</Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by name, email, or company..."
                  className="bg-theme-surface border-theme text-foreground pl-9"
                  data-testid="custom-plan-user-search"
                />
                {userResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border border-theme rounded-lg shadow-lg max-h-48 overflow-y-auto overscroll-contain touch-pan-y">
                    {userResults.map((u) => (
                      <button
                        key={u.user_id}
                        className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2 text-sm"
                        onClick={() => { setSelectedUser(u); setUserSearch(""); setUserResults([]); }}
                        data-testid={`user-option-${u.user_id}`}
                      >
                        <div className="h-7 w-7 rounded-full bg-[#0046BE]/20 flex items-center justify-center text-xs font-bold text-[#0046BE]">
                          {(u.name || "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-foreground font-medium truncate">{u.name}</div>
                          <div className="text-muted-foreground text-xs truncate">{u.email}{u.company_name ? ` \u2022 ${u.company_name}` : ""}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {userSearch.length >= 2 && userResults.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border border-theme rounded-lg shadow-lg p-3 text-center text-muted-foreground text-sm">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>

          <PlanFeatureForm formData={customFormData} setFormData={setCustomFormData} />

          {/* Notes */}
          <div>
            <Label className="text-muted-foreground">Internal Notes (optional)</Label>
            <Input value={customFormData.notes} onChange={(e) => setCustomFormData({ ...customFormData, notes: e.target.value })}
              className="bg-theme-surface border-theme text-foreground mt-1" placeholder="e.g., Special deal for Q1 2026" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDialogOpen(false)} className="border-theme text-foreground">Cancel</Button>
            <Button onClick={saveCustomPlan} disabled={customSaving}
              className="bg-gradient-to-r from-[#FFCC00] to-amber-500 text-black font-semibold hover:opacity-90"
              data-testid="save-custom-plan-btn"
            >
              {customSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : "Create & Sync to Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Custom Plan Limits Dialog ─── */}
      <Dialog open={limitsDialogOpen} onOpenChange={setLimitsDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-md" data-testid="edit-limits-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#FFCC00]" /> Edit Limits
            </DialogTitle>
            {limitsEditingPlan && (
              <p className="text-xs text-muted-foreground mt-1">
                {limitsEditingPlan.name}{limitsEditingPlan.assigned_user_email ? ` · ${limitsEditingPlan.assigned_user_email}` : ""}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Changes apply immediately. Pricing and Stripe product stay untouched.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground text-xs flex items-center gap-1"><Users className="w-3 h-3" /> Team Members</Label>
                <Input type="number" min={0}
                  value={limitsForm.max_users ?? ""}
                  onChange={(e) => setLimitsForm({ ...limitsForm, max_users: e.target.value })}
                  className="bg-theme-surface border-theme mt-1"
                  data-testid="limits-max-users" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Phone Lines</Label>
                <Input type="number" min={0}
                  value={limitsForm.dedicated_lines ?? ""}
                  onChange={(e) => setLimitsForm({ ...limitsForm, dedicated_lines: e.target.value })}
                  className="bg-theme-surface border-theme mt-1"
                  data-testid="limits-dedicated-lines" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS / month</Label>
                <Input type="number" min={0}
                  value={limitsForm.sms_limit ?? ""}
                  onChange={(e) => setLimitsForm({ ...limitsForm, sms_limit: e.target.value })}
                  className="bg-theme-surface border-theme mt-1"
                  data-testid="limits-sms" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Voice minutes / month</Label>
                <Input type="number" min={0}
                  value={limitsForm.minutes_limit ?? ""}
                  onChange={(e) => setLimitsForm({ ...limitsForm, minutes_limit: e.target.value })}
                  className="bg-theme-surface border-theme mt-1"
                  data-testid="limits-minutes" />
              </div>
              <div className="col-span-2">
                <Label className="text-muted-foreground text-xs flex items-center gap-1"><Building2 className="w-3 h-3 text-emerald-500" /> Max Locations</Label>
                <Input type="number" min={1}
                  value={limitsForm.max_locations ?? ""}
                  onChange={(e) => setLimitsForm({ ...limitsForm, max_locations: e.target.value })}
                  className="bg-theme-surface border-theme mt-1"
                  data-testid="limits-max-locations" />
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Internal notes (optional)</Label>
              <Textarea
                value={limitsForm.notes ?? ""}
                onChange={(e) => setLimitsForm({ ...limitsForm, notes: e.target.value })}
                className="bg-theme-surface border-theme mt-1"
                rows={2}
                data-testid="limits-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-theme">
            <Button variant="outline" onClick={() => setLimitsDialogOpen(false)} className="border-theme text-foreground">Cancel</Button>
            <Button onClick={saveLimits} disabled={limitsSaving} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 font-semibold" data-testid="limits-save-btn">
              {limitsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Limits
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Checkout Link Dialog ─── */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#FFCC00]" /> Payment Link
            </DialogTitle>
          </DialogHeader>
          {linkLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" />
              <span className="ml-3 text-muted-foreground">Generating Stripe checkout link...</span>
            </div>
          ) : checkoutUrl ? (
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">Send this link to the user. When they complete checkout, their subscription will be activated automatically.</p>
              {linkPromoApplied && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <Tag className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-medium">Promo "{linkPromoApplied}" applied</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input value={checkoutUrl} readOnly className="bg-theme-surface border-theme text-foreground text-sm" data-testid="checkout-link-input" />
                <Button variant="outline" size="icon" onClick={copyLink} className="shrink-0 border-theme" data-testid="copy-checkout-link">
                  <Copy className="w-4 h-4" />
                </Button>
                <a href={checkoutUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="shrink-0 border-theme" data-testid="open-checkout-link">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">Generate a Stripe checkout link for this custom plan. Optionally apply a promo code.</p>
              <div className="space-y-1.5">
                <label className="text-muted-foreground text-xs font-medium">Promo / Coupon Code (optional)</label>
                <Input
                  value={linkPromoCode} onChange={(e) => setLinkPromoCode(e.target.value)}
                  placeholder="e.g. SAVE20, WELCOME10..."
                  className="bg-theme-surface border-theme text-foreground"
                  data-testid="custom-plan-promo-input"
                />
                <p className="text-muted-foreground text-[11px]">
                  {linkPromoCode.trim() ? "This promo code will be pre-applied to the checkout." : "If left empty, the user can enter a promo code during checkout."}
                </p>
              </div>
              <Button onClick={confirmGenerateLink} className="w-full bg-gradient-to-r from-[#FFCC00] to-amber-500 text-black hover:opacity-90 font-semibold" data-testid="generate-link-btn">
                <Link2 className="w-4 h-4 mr-2" /> Generate Payment Link
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)} className="border-theme text-foreground">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPlans;
